import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Button, Select, Checkbox, Row, Col, Tooltip } from 'antd';
import {
  BulkInviteOperationFunc,
  InviteUsersToCustomerParam,
  UserProfileInfo,
  BulkRevokeOperationFunc,
  RevokeUsersFromCustomerParam,
  Permission,
  CiPermission,
  LoginUserProfile,
  EditOperationFunc,
  FormValues,
  FormItemKey,
} from '../../types';
import { useGetUserMeta } from '@src/components/user-management/hooks';
import { Loading } from '@src/components/common';
import {
  ActionResponseTitle,
  EmailsSelect,
  IconWrapper,
  StatusContainer,
  StatusContent,
  StatusTitle,
  StyledForm,
} from './styled';
import { CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { getUserProfile } from '@src/store/common/selectors';
import {
  defaultProfileForEditor,
  formValuesToProfileInfo,
  groupActionResponse,
  profileInfoToFormValues,
  validateEmails,
  CI_PERMISSION_KEYS,
  OperationStatusInfo,
  mergeWithCiPermission,
  mergeWithNonCiPermission,
  getFirstLevelPermissions,
  getKvBooleanPermissionDescriptors,
  getKvBooleanPermissionKeys,
  PERMISSION_NO_GRANT_TOOLTIP,
  USER_ROLE_ADMIN,
  USER_ROLE_STAFF,
} from './utils';
import { NexaPermissionTooltipContent } from './tooltipContent';
import {
  getKvPermissionCheckboxState,
  getRoleNameById,
  filterPermissionForDisabledFlag,
} from './contextCenterEligibility';
import { isString } from 'lodash';
import { CiPermissionSelect } from './CiPermissionSelect';
import { capitalizeFirstLetter } from '@src/components/filter/components/Management/SavedFilterList/utils';
import {
  INVITE_EMAIL_TOKEN_SEPARATORS,
  MAX_ACCOUNTS_COUNT_TO_OPERATE,
  MAX_USER_COUNT_TO_OPERATE,
  normalizeInviteEmailTags,
} from '../utils';
import { useFeature } from '@src/globalStore/featureStore';
import featureNames from '@src/utils/featureToggle/constants';
import { ADMIN_ROLE_ID, API_MANAGEMENT_PERMISSION, NEXA_PERMISSION } from '@src/utils/constants';

type Action = 'invite' | 'edit' | 'revoke';
interface Props {
  action: Action;
  /** customize form items that will include */
  includeFormItems?: FormItemKey[];
  /** customize form items that will exclude */
  excludeFormItems?: FormItemKey[];
  profile?: UserProfileInfo;
  usersToRevoke?: UserProfileInfo[];
  onCreate?: BulkInviteOperationFunc;
  onRevoke?: BulkRevokeOperationFunc;
  onEdit?: EditOperationFunc;
  onCancel: () => void;
}

const okBtnStyle: {
  [key in Action]: {
    danger: boolean;
    label: string;
  };
} = {
  invite: {
    danger: false,
    label: 'Send Invitations',
  },
  revoke: {
    danger: true,
    label: 'Revoke',
  },
  edit: {
    danger: false,
    label: 'Update',
  },
};

export const UserProfileEditor = ({
  action,
  includeFormItems,
  excludeFormItems,
  profile,
  usersToRevoke,
  onEdit,
  onCreate,
  onRevoke,
  onCancel,
}: Props) => {
  const operatorProfile = useSelector<any, LoginUserProfile>(getUserProfile);
  const {
    isFetching,
    data: { roleList = [], accountList = [], permissionDefinitions = [], canGrant = {} } = {},
  } = useGetUserMeta();
  // Per-permission grantability from the server (map ticket T5 / ISSUE-2). The client holds NO
  // grant logic — a permission is grantable unless the server explicitly says canGrant:false.
  const canGrantPermission = useCallback((key: string) => canGrant[key] !== false, [canGrant]);
  // Registry-defined boolean permissions (KV-stored) rendered generically — no hardcoded entry.
  // Context Center is omitted unless nexa-context-center is on for this operator.
  const contextCenterEnabled = useFeature(featureNames.NEXA_CONTEXT_CENTER);
  const kvBooleanDescriptors = useMemo(
    () =>
      filterPermissionForDisabledFlag(
        getKvBooleanPermissionDescriptors(permissionDefinitions),
        contextCenterEnabled,
      ),
    [permissionDefinitions, contextCenterEnabled],
  );
  const kvBooleanKeys = useMemo(
    () => kvBooleanDescriptors.map((def) => def.key),
    [kvBooleanDescriptors],
  );
  const nexaFeatureEnabled = useFeature(featureNames.NEXA);
  const { can_manage_nexa: showNexaEnabledOption } = operatorProfile;

  const mergedRoleList = useMemo(() => {
    if (profile) {
      const { roleId, roleName } = profile;
      if (roleId && !roleList.find((it) => it.roleId === roleId)) {
        return [...roleList, { roleId, roleName }];
      }
    }
    return roleList;
  }, [profile, roleList]);

  const mergedAccountList = useMemo(() => {
    if (operatorProfile) {
      const { customer_id, customer_name } = operatorProfile;
      if (!accountList.find((it) => it.id === customer_id)) {
        return [...accountList, { id: customer_id, name: customer_name }];
      }
    }
    return accountList;
  }, [accountList, operatorProfile]);

  const shouldShowFormItem = useCallback(
    (key: FormItemKey) => {
      const filter = (k: FormItemKey) => {
        if (Array.isArray(includeFormItems)) {
          return includeFormItems.includes(k);
        } else if (Array.isArray(excludeFormItems)) {
          return !excludeFormItems.includes(k);
        }
        return true;
      };
      switch (key) {
        case 'email':
          return true;
        case 'hidden':
          // Was isInternal(operatorProfile); now driven by the server's canGrant['hidden']
          // (grant_rule: conviva-internal) — no duplicated client grant check (T5/ISSUE-2).
          return ['invite', 'edit'].includes(action) && canGrantPermission('hidden') && filter(key);
        case 'roleId':
          return (
            ['invite', 'edit'].includes(action) && mergedRoleList?.length > 0 && filter('roleId')
          );
        case 'accounts':
          return (
            ['invite', 'revoke'].includes(action) &&
            mergedAccountList.length > 0 &&
            filter('accounts')
          );
        case 'permissions':
          return ['invite', 'edit'].includes(action) && filter('permissions');
        case 'nexaPermission':
          return ['invite', 'edit'].includes(action) && filter('nexaPermission');
        case 'apiManagementPermission':
          // Was checkCanEditApiManagement(); now driven by the server's
          // canGrant['apiManagementPermission'] (grant_rule requiresPermission EDIT) — T5/ISSUE-2.
          return (
            ['invite', 'edit'].includes(action) &&
            canGrantPermission('apiManagementPermission') &&
            filter('apiManagementPermission')
          );
      }
    },
    [
      action,
      excludeFormItems,
      includeFormItems,
      mergedAccountList.length,
      mergedRoleList?.length,
      operatorProfile,
      canGrantPermission,
    ],
  );
  const [form] = Form.useForm<FormValues>();
  const roleId = Form.useWatch('roleId', form);

  // NEX-405: the watched (possibly unsaved) role, resolved to its name for the
  // Context Center eligibility rule. mergedRoleList includes the target's own role.
  const targetRoleName = useMemo(
    () => getRoleNameById(mergedRoleList, roleId),
    [mergedRoleList, roleId],
  );

  // Reset nexaPermission if role changes from admin to non-admin and Enable was selected
  useEffect(() => {
    if (roleId !== ADMIN_ROLE_ID) {
      const currentNexaPermission = form.getFieldValue('nexaPermission');
      if (currentNexaPermission === NEXA_PERMISSION.ADMIN) {
        form.setFieldValue('nexaPermission', NEXA_PERMISSION.USAGE);
      }
    }
  }, [roleId, form]);

  // On invite, keep the API Management default in sync with the selected role
  // (Admin -> Edit, Staff -> View, others -> Hide) until the operator overrides it.
  const apiPermTouchedRef = useRef(false);
  const roleToApiManagementDefault = useCallback((rid?: number) => {
    if (rid === USER_ROLE_ADMIN || rid === USER_ROLE_STAFF) {
      return API_MANAGEMENT_PERMISSION.EDIT;
    }
    return API_MANAGEMENT_PERMISSION.NONE;
  }, []);
  useEffect(() => {
    if (action !== 'invite' || apiPermTouchedRef.current) {
      return;
    }
    form.setFieldValue('apiManagementPermission', roleToApiManagementDefault(roleId));
  }, [action, roleId, form, roleToApiManagementDefault]);

  useEffect(() => {
    if (action === 'revoke') {
      form.setFieldValue(
        'email',
        (usersToRevoke ?? []).map((it) => it.email),
      );
    } else {
      const initProfile = profile ?? defaultProfileForEditor;
      form.setFieldsValue(
        profileInfoToFormValues(initProfile, [operatorProfile.customer_id], kvBooleanKeys),
      );
    }
  }, [action, form, profile, operatorProfile.customer_id, usersToRevoke, kvBooleanKeys]);
  const [actionResponse, setActionResponse] = useState<OperationStatusInfo[]>([]);
  const [requesting, setRequesting] = useState(false);
  const onFinish = useCallback(
    async (values) => {
      const info = formValuesToProfileInfo(values, operatorProfile, kvBooleanKeys);
      if (action === 'edit') {
        if (onEdit && profile) {
          try {
            setRequesting(true);
            const { profile: profileInfo } = info;
            await onEdit({
              props: { ...(profileInfo ?? {}), email: profile?.email },
              userId: profile.userId,
            });
          } finally {
            setRequesting(false);
          }
        }
      } else if (action === 'invite') {
        const { accounts, emails, profile: profileInfo } = info;
        const payload: InviteUsersToCustomerParam[] = accounts.map((id) => {
          return {
            customer: { id },
            users: emails.map((it) => {
              return {
                ...profileInfo,
                email: it,
              };
            }),
          };
        });
        if (onCreate) {
          try {
            setRequesting(true);
            const inviteResult = await onCreate(payload);
            if (inviteResult) {
              setActionResponse(groupActionResponse(inviteResult));
            }
            // reset to default state after invite success
            apiPermTouchedRef.current = false;
            form.setFieldsValue(
              profileInfoToFormValues(
                defaultProfileForEditor,
                [operatorProfile.customer_id],
                kvBooleanKeys,
              ),
            );
          } finally {
            setRequesting(false);
          }
        }
      } else if (action === 'revoke') {
        const { accounts, emails } = info;
        const payload: RevokeUsersFromCustomerParam[] = accounts.map((id) => {
          return {
            customer: { id },
            users: emails,
          };
        });
        if (onRevoke) {
          try {
            setRequesting(true);
            const inviteResult = await onRevoke(payload);
            if (inviteResult) {
              setActionResponse(groupActionResponse(inviteResult));
            }
            // reset to default state after revoke success
            form.setFieldsValue(
              profileInfoToFormValues(defaultProfileForEditor, [], kvBooleanKeys),
            );
          } finally {
            setRequesting(false);
          }
        }
      }
    },
    [operatorProfile, action, onEdit, profile, onCreate, form, onRevoke, kvBooleanKeys],
  );
  const { isActionDone, isPartialSuccess } = useMemo(() => {
    const isActionDone = actionResponse.length > 0;
    const isPartialSuccess = actionResponse.filter((it) => it.state !== 'success').length > 0;
    return { isActionDone, isPartialSuccess };
  }, [actionResponse]);
  if (isFetching) {
    return <Loading />;
  }
  return (
    <div>
      <div
        style={{
          maxWidth: '800px',
          margin: '20px auto',
        }}
      >
        {isActionDone && (
          <div>
            {isPartialSuccess ? (
              [
                <ActionResponseTitle key="summary">
                  {capitalizeFirstLetter(action)} completed with some{' '}
                  <span className="error">errors</span>:
                </ActionResponseTitle>,
                actionResponse
                  .filter((it) => it.state !== 'success')
                  .map((it) => {
                    const { title, content, state } = it;
                    return (
                      <OperationStatus
                        key={state + content}
                        state={state}
                        title={title}
                        content={content}
                      ></OperationStatus>
                    );
                  }),
              ]
            ) : (
              <ActionResponseTitle>
                {`${capitalizeFirstLetter(action)}d all users successfully!`}
              </ActionResponseTitle>
            )}
          </div>
        )}
        <StyledForm form={form as any} layout="vertical" onFinish={onFinish}>
          {action === 'edit' && profile ? (
            <div
              style={{
                fontSize: '18px',
                marginBottom: '14px',
                fontWeight: 600,
                color: 'rgba(0, 0, 0, 0.88)',
              }}
            >
              Modify User: {profile.email}
            </div>
          ) : (
            <Form.Item
              label={`Email Addresses (Add up to ${MAX_USER_COUNT_TO_OPERATE} emails; separate with comma, semicolon, space, tab, or line breaks)`}
              name="email"
              normalize={normalizeInviteEmailTags}
              rules={[{ required: true, validator: validateEmails }]}
              style={{ paddingBottom: '6px', marginBottom: '8px' }}
            >
              {/* <Input.TextArea placeholder="Enter emails separated by commas or newlines" /> */}
              <EmailsSelect
                mode="tags"
                tokenSeparators={[...INVITE_EMAIL_TOKEN_SEPARATORS]}
                allowClear
                optionFilterProp="title"
                placeholder="Separate with comma, semicolon, space, tab, or line breaks"
              ></EmailsSelect>
            </Form.Item>
          )}
          {shouldShowFormItem('hidden') && (
            <Form.Item name="hidden" valuePropName="checked" style={{ marginBottom: '10px' }}>
              <Checkbox>For Conviva internal view only</Checkbox>
            </Form.Item>
          )}
          {shouldShowFormItem('roleId') && (
            <Form.Item label="Role" name="roleId">
              <Select>
                {mergedRoleList.map((it) => {
                  const { roleId, roleName } = it;
                  return (
                    <Select.Option value={roleId} key={roleId}>
                      {roleName}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
          )}
          {/* Only show account list for invite user */}
          {shouldShowFormItem('accounts') && (
            <Form.Item
              label={`Accounts (Max ${MAX_ACCOUNTS_COUNT_TO_OPERATE})`}
              name="accounts"
              rules={[
                {
                  required: true,
                  validator: (_, value) => {
                    if (!Array.isArray(value) || value.length === 0) {
                      return Promise.reject('Please select at least one account');
                    }
                    if (value.length > MAX_ACCOUNTS_COUNT_TO_OPERATE) {
                      return Promise.reject('Max limit reached.');
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Select
                mode="multiple"
                placeholder="select account"
                filterOption={(input, option) => {
                  if (isString(option?.label)) {
                    return option?.label?.toLowerCase().includes(input.toLowerCase());
                  }
                  return false;
                }}
                options={[
                  {
                    disabled: true,
                    label: (
                      <div
                        style={{
                          cursor: 'default',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          gap: '10px',
                        }}
                      >
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => {
                            form.setFieldValue(
                              'accounts',
                              mergedAccountList.map((it) => it.id),
                            );
                          }}
                        >
                          Select All
                        </Button>
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => {
                            form.setFieldValue('accounts', []);
                          }}
                        >
                          Clear All
                        </Button>
                      </div>
                    ),
                  },
                  ...mergedAccountList.map((it) => ({
                    value: it.id,
                    label: it.name,
                  })),
                ]}
                allowClear
              ></Select>
            </Form.Item>
          )}
          {shouldShowFormItem('permissions') && (
            <Form.Item
              label="Permissions"
              name="permissions"
              getValueProps={(permissions: FormValues['permissions']) => {
                return {
                  value: (permissions ?? []).filter(
                    (it) => !CI_PERMISSION_KEYS.includes(it as CiPermission),
                  ),
                };
              }}
              getValueFromEvent={(value) => {
                const permissions = form.getFieldValue('permissions') as Permission[];
                const merged = mergeWithNonCiPermission(permissions, value as Permission[]);
                return merged;
              }}
            >
              <Checkbox.Group
                style={{
                  width: '100%',
                }}
              >
                <Row gutter={[15, 15]}>
                  {Object.entries(getFirstLevelPermissions(operatorProfile)).map(([key, value]) => {
                    return (
                      <Col span={12} key={key}>
                        {key === 'ciEnabled' ? (
                          <CiPermissionSelect
                            form={form}
                            onCiPermissionChange={(values) => {
                              const permissions = form.getFieldValue('permissions') as Permission[];
                              form.setFieldValue(
                                'permissions',
                                mergeWithCiPermission(permissions, values as CiPermission[]),
                              );
                            }}
                          />
                        ) : (
                          <Tooltip
                            title={
                              canGrantPermission(key) ? undefined : PERMISSION_NO_GRANT_TOOLTIP
                            }
                          >
                            <Checkbox
                              value={key}
                              disabled={!canGrantPermission(key)}
                              style={{ fontWeight: 'normal' }}
                            >
                              {value}
                            </Checkbox>
                          </Tooltip>
                        )}
                      </Col>
                    );
                  })}
                  {/* Registry-defined boolean permissions (KV) rendered generically from
                      /users/meta. Context Center is omitted unless nexa-context-center is on,
                      and disabled for target roles outside the Admin and Staff tiers (NEX-405). */}
                  {kvBooleanDescriptors.map((def) => {
                    const kvState = getKvPermissionCheckboxState(
                      def,
                      canGrantPermission(def.key),
                      targetRoleName,
                    );
                    return (
                      <Col span={12} key={def.key}>
                        <Tooltip title={kvState.tooltip}>
                          <Checkbox
                            value={def.key}
                            disabled={kvState.disabled}
                            style={{ fontWeight: 'normal' }}
                          >
                            {def.label}
                          </Checkbox>
                        </Tooltip>
                      </Col>
                    );
                  })}
                </Row>
              </Checkbox.Group>
            </Form.Item>
          )}
          {nexaFeatureEnabled && shouldShowFormItem('nexaPermission') && (
            <Form.Item
              label={
                <span>
                  Nexa Permission{' '}
                  <Tooltip
                    title={
                      <NexaPermissionTooltipContent showNexaEnabledOption={showNexaEnabledOption} />
                    }
                  >
                    <InfoCircleOutlined style={{ cursor: 'pointer', color: '#1890ff' }} />
                  </Tooltip>
                </span>
              }
              name="nexaPermission"
            >
              <Select
                placeholder="Select Nexa permission level"
                allowClear
                style={{ width: '200px' }}
              >
                <Select.Option value={NEXA_PERMISSION.NO}>No</Select.Option>
                <Select.Option value={NEXA_PERMISSION.USAGE}>Use</Select.Option>
                <Select.Option
                  value={NEXA_PERMISSION.ADMIN}
                  disabled={!showNexaEnabledOption || roleId !== ADMIN_ROLE_ID}
                >
                  Enable
                </Select.Option>
              </Select>
            </Form.Item>
          )}
          {shouldShowFormItem('apiManagementPermission') && (
            <Form.Item label="API Management" name="apiManagementPermission">
              <Select
                placeholder="Select API Management permission level"
                allowClear
                style={{ width: '200px' }}
                onChange={() => {
                  apiPermTouchedRef.current = true;
                }}
              >
                <Select.Option value={null}>Hide</Select.Option>
                <Select.Option value={API_MANAGEMENT_PERMISSION.VIEW}>View</Select.Option>
                <Select.Option value={API_MANAGEMENT_PERMISSION.EDIT}>Edit</Select.Option>
              </Select>
            </Form.Item>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
            }}
          >
            <Button onClick={() => onCancel && onCancel()}>Back</Button>
            <Button
              type="primary"
              danger={okBtnStyle[action].danger}
              htmlType="submit"
              loading={requesting}
            >
              {okBtnStyle[action].label}
            </Button>
          </div>
        </StyledForm>
      </div>
    </div>
  );
};

const OperationStatus = ({ state, title, content }: OperationStatusInfo) => {
  return (
    <StatusContainer>
      <IconWrapper>
        {state === 'success' ? (
          <CheckCircleOutlined style={{ color: 'rgb(34 197 94' }} />
        ) : (
          <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
        )}
      </IconWrapper>
      <div>
        <StatusTitle>{title}</StatusTitle>
        <StatusContent>{content}</StatusContent>
      </div>
    </StatusContainer>
  );
};
